// frontend/src/pages/CodeEditor.tsx

import { useState } from 'react';
import { usePyodide, RunResult } from '../hooks/usePyodide';
import { geminiService, GeminiApiError } from '../services/gemini.service';
import { CodeEditorPanel } from '../components/code-editor/CodeEditorPanel';
import { OutputPanel } from '../components/code-editor/OutputPanel';
import { EvaluationPanel } from '../components/code-editor/EvaluationPanel';
import { DEFAULT_STARTER_CODE } from '../types/codeEditor.types';
import { DetailedCodeEvaluationResult } from '../types/ai.types';
import '../styles/code-editor.css';

import { usePlanUsage } from '../hooks/usePlanUsage';
import { PlanLimitModal } from '../components/common/PlanLimitModal';

export function CodeEditorPage() {
  const { isReady, isLoading: pyodideLoading, loadError, runPython } = usePyodide();

  const [code, setCode] = useState(DEFAULT_STARTER_CODE);
  const [runResult, setRunResult] = useState<RunResult | null>(null);
  const [isRunning, setIsRunning] = useState(false);

  const [evalResult, setEvalResult] = useState<DetailedCodeEvaluationResult | null>(null);
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [evalError, setEvalError] = useState<string | null>(null);
  const [showLimitModal, setShowLimitModal] = useState(false);

  const { isAtLimit, refetch: refetchUsage } = usePlanUsage();

  async function handleRun() {
    if (!isReady) return;
    setIsRunning(true);
    const result = await runPython(code);
    setRunResult(result);
    setIsRunning(false);
  }

  function handleReset() {
    setCode(DEFAULT_STARTER_CODE);
    setRunResult(null);
    setEvalResult(null);
    setEvalError(null);
  }

  function handleClearOutput() {
    setRunResult(null);
  }

  async function handleEvaluate() {
    if (isAtLimit('code_evaluation')) {
      setShowLimitModal(true);
      return;
    }
    setIsEvaluating(true);
    setEvalError(null);
    try {
      const result = await geminiService.evaluateCodeDetailed(code, runResult?.output ?? null);
      setEvalResult(result);
      refetchUsage();
    } catch (err: any) {
      if (err?.status === 402 || err?.code === 'QUOTA_EXCEEDED' || err?.message?.includes('limit')) {
        setShowLimitModal(true);
      } else {
        setEvalError(err instanceof GeminiApiError ? err.message : 'Değerlendirme başarısız oldu.');
      }
    } finally {
      setIsEvaluating(false);
    }
  }

  return (
    <div className="code-editor-page">
      <header className="code-editor-header">
        <h1>Kod Editörü 💻</h1>
        <p>Browser'da çalışan mini IDE — Kod yaz, çalıştır, AI'dan değerlendirme al</p>
      </header>

      <div className="grid grid-2 code-editor-grid">
        <CodeEditorPanel
          code={code}
          onChange={setCode}
          onRun={handleRun}
          onReset={handleReset}
          isRunning={isRunning || pyodideLoading}
        />

        <div>
          <OutputPanel
            result={runResult}
            pyodideLoading={pyodideLoading}
            pyodideError={loadError}
            onClear={handleClearOutput}
          />

          <EvaluationPanel
            result={evalResult}
            isLoading={isEvaluating}
            error={evalError}
            onEvaluate={handleEvaluate}
            canEvaluate={code.trim().length > 0}
          />
        </div>
      </div>

      <PlanLimitModal
        type="code_evaluation"
        isOpen={showLimitModal}
        onClose={() => setShowLimitModal(false)}
      />
    </div>
  );
}
